import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class GoogleEarthService {

  /**
   * Generates a KML (Keyhole Markup Language) string representing flight routes and optionally airport markers.
   *
   * @param routes - An array of route objects, each containing information about departure and arrival stations, their coordinates, and route status (e.g., isDeadhead, isCancelled).
   * @param addMarkers - If true, adds airport markers for each unique departure and arrival station.
   * @returns A string containing the KML representation of the provided routes and markers, suitable for use in Google Earth or other KML-compatible applications.
   *
   * @remarks
   * - Filters out routes that are deadhead or cancelled, unless the departure and arrival stations are the same.
   * - Ensures that only valid routes with non-zero, numeric coordinates are included.
   * - Avoids duplicate route lines and airport markers.
   * - The generated KML includes a camera view, styled lines for routes, and airport icons for markers.
   */
  generateKml(routes: any[], labels: boolean, gLineColor: string, icons: boolean): string {
    const gxLineColor = this.convertColorToKml(gLineColor);
    
    const kmlHeader = `<?xml version="1.0" encoding="UTF-8"?>
    <kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
      <name>Flight Routes</name>
      <Camera>
        <longitude>-98.5795</longitude>
        <latitude>39.8283</latitude>
        <altitude>9000000</altitude>
        <heading>0</heading>
        <tilt>0</tilt>
        <roll>0</roll>
        <altitudeMode>absolute</altitudeMode>
      </Camera>`;

    const kmlFooter = `
      </Document>
    </kml>`;

    const seenRoutes = new Set<string>();
    const seenAirports = new Set<string>();
    const placemarks: string[] = [];

    /** Filter out deadhead and cancelled routes and routes
     * where departure and arrival stations are the same.
     */
    const validRoutes = routes.filter( (route) => {
      return (!route.isDeadhead && !route.isCancelled)})

    for (const route of validRoutes) {
      const dep = {
        station: route.departureStation,
        latitude: route.departureStation_Latitude,
        longitude: route.departureStation_Longitude
      };
      
      const arr = {
        station: route.arrivalStation,
        latitude: route.arrivalStation_Latitude,
        longitude: route.arrivalStation_Longitude
      };
      const routeName = `${dep.station} to ${arr.station}`;

      // Ensure all required fields exist
      if (
        !dep.latitude || !dep.longitude || 
        !arr.latitude || !arr.longitude ||
        isNaN(dep.latitude) || isNaN(dep.longitude) ||
        isNaN(arr.latitude) || isNaN(arr.longitude) ||
        (dep.latitude === 0 && dep.longitude === 0) ||
        (arr.latitude === 0 && arr.longitude === 0 ||
        
        dep.station === arr.station)) {
        continue;
      }

      //See below notes on usage of this method.
      //const arcCoordinates = this.generateArcCoordinates(dep, arr);
      const routeKey = `${dep.station}->${arr.station}`;
      if (seenRoutes.has(routeKey)) continue;
      seenRoutes.add(routeKey);

      placemarks.push( `
        <Placemark>
          <name>${routeName}</name>
          <description>Flight Route ${routeName}</description>
          <Style>
            <LineStyle>
              <color>${gxLineColor}</color>
              <width>2</width>
            </LineStyle>
          </Style>
          <LineString>
            <tessellate>1</tessellate>
            <altitudeMode>clampToGround</altitudeMode>
            <coordinates>
              ${dep.longitude},${dep.latitude},0
              ${arr.longitude},${arr.latitude},0
            </coordinates>
          </LineString>
        </Placemark>`);

        if (labels && icons) {
          if (!seenAirports.has(dep.station)) {
            seenAirports.add(dep.station);
            placemarks.push(`
              <!-- Departure airport icon -->
              <Placemark>
              <name>${route.departureStation}</name>
                <Style>
                  <IconStyle>
                    <scale>1.1</scale>
                    <Icon>
                      <href>http://maps.google.com/mapfiles/kml/shapes/airports.png</href>
                    </Icon>
                  </IconStyle>
                </Style>
                <Point>
                  <altitudeMode>clampToGround</altitudeMode>
                  <coordinates>${dep.longitude},${dep.latitude},0</coordinates>
                </Point>
              </Placemark>
              `);
          }
  
          if (!seenAirports.has(arr.station)) {
            seenAirports.add(arr.station);
            placemarks.push(`
              <!-- Arrival airport icon -->
              <Placemark>
                <name>${route.arrivalStation}</name>
                <Style>
                  <IconStyle>
                    <scale>1.1</scale>
                    <Icon>
                      <href>http://maps.google.com/mapfiles/kml/shapes/airports.png</href>
                    </Icon>
                  </IconStyle>
                </Style>
                <Point>
                  <altitudeMode>clampToGround</altitudeMode>
                  <coordinates>${arr.longitude},${arr.latitude},0</coordinates>
                </Point>
              </Placemark>`);
          }
        }

        else if (labels && !icons) {
          if (!seenAirports.has(dep.station)) {
            seenAirports.add(dep.station);
            placemarks.push(`
              <!-- Departure airport icon -->
              <Placemark>
                <name>${route.departureStation}</name>
                <Style>
                  <IconStyle>
                    <Icon>
                      <href>http://maps.google.com/mapfiles/kml/shapes/airports.png</href>
                    </Icon>
                    <scale>0</scale> <!-- Ensures even if image fails, nothing is shown -->
                  </IconStyle>
                  <LabelStyle>
                    <scale>1.1</scale> <!-- optional: adjust text size -->
                  </LabelStyle>
                </Style>
                <Point>
                  <altitudeMode>clampToGround</altitudeMode>
                  <coordinates>${dep.longitude},${dep.latitude},0</coordinates>
                </Point>
              </Placemark>
              `);
          }
  
          if (!seenAirports.has(arr.station)) {
            seenAirports.add(arr.station);
            placemarks.push(`
              <!-- Arrival airport icon -->
              <Placemark>
                <name>${route.arrivalStation}</name>
                <Style>
                <IconStyle>
                    <Icon>
                      <href>http://maps.google.com/mapfiles/kml/shapes/airports.png</href>
                    </Icon>
                    <scale>0</scale> <!-- Ensures even if image fails, nothing is shown -->
                  </IconStyle>
                  <LabelStyle>
                    <scale>1.1</scale> <!-- optional: adjust text size -->
                  </LabelStyle>
                </Style>
                <Point>
                  <altitudeMode>clampToGround</altitudeMode>
                  <coordinates>${arr.longitude},${arr.latitude},0</coordinates>
                </Point>
              </Placemark>`);
          }
        }
    };
    //console.log(kmlHeader + placemarks.join('') + kmlFooter);
    return kmlHeader + placemarks.join('') + kmlFooter;
  }
  
  /**
   * Converts a hex color string to KML format (ARGB).
   * KML uses ARGB format, so the alpha channel is set to 'ff' (fully opaque).
   *
   * @param hexColor - The hex color string (e.g., '#FF5733').
   * @returns A string in KML ARGB format (e.g., 'ff5733ff').
   */
  convertColorToKml(hexColor: string): string {
    const bgColorFFmpeg = hexColor.replace('#', '').trim();
    const r = bgColorFFmpeg.slice(0, 2);
    const g = bgColorFFmpeg.slice(2, 4);
    const b = bgColorFFmpeg.slice(4, 6);
    return `ff${b}${g}${r}`; // KML uses ARGB format
  }

  //! NOT USED - Possible future use for mid point altitude
  /** Create a mid point altitude, this wokrs but makes the line
   * look like a chevron going up to altitude of 30,000 feek (9144m).
   */
  // midpointWithAltitude(dep: any, arr: any, altitude: number): string {
  //   const lat1 = dep.latitude;
  //   const lon1 = dep.longitude;
  //   const lat2 = arr.latitude;
  //   const lon2 = arr.longitude;

  //   const midLat = (lat1 + lat2) / 2;
  //   const midLon = (lon1 + lon2) / 2;

  //   return `${midLon},${midLat},${altitude}`;
  // }

    //! NOT USED - Possible future use for mid point altitude
  /** Method that will create a specified number of segments from
   * departure to arrival coordinates and creates an arc if there 
   * are enough segments.  Google Earth hangs when trying to load
   * too many segments, so this is limited to 3 segments, but still
   * does not work on Google Earth as its to many points to draw.
   */
  // generateArcCoordinates(
  //   dep: any, arr: any, 
  //   segments: number = 3,
  //   baseAltitude: number = 3000, //meters
  //   maxAltitude: number = 9144 //meters
  // ): string {
  //   const toRadians = (deg: number) => deg * Math.PI / 180;
  //   const toDegrees = (rad: number) => rad * 180 / Math.PI;

  //   const lat1 = toRadians(dep.latitude);
  //   const lon1 = toRadians(dep.longitude);
  //   const lat2 = toRadians(arr.latitude);
  //   const lon2 = toRadians(arr.longitude);

  //   const d = 2 * Math.asin(Math.sqrt(
  //     Math.pow(Math.sin((lat2 - lat1) / 2), 2) +
  //     Math.cos(lat1) * Math.cos(lat2) *
  //     Math.pow(Math.sin((lon2 - lon1) / 2), 2)
  //   ));

  //   const coords: string[] = [];

  //   for (let i = 0; i <= segments; i++) {
  //     const f = i / segments;
  //     const A = Math.sin((1 - f) * d) / Math.sin(d);
  //     const B = Math.sin(f * d) / Math.sin(d);

  //     const x = A * Math.cos(lat1) * Math.cos(lon1) + B * Math.cos(lat2) * Math.cos(lon2);
  //     const y = A * Math.cos(lat1) * Math.sin(lon1) + B * Math.cos(lat2) * Math.sin(lon2);
  //     const z = A * Math.sin(lat1) + B * Math.sin(lat2);

  //     const lat = toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y)));
  //     const lon = toDegrees(Math.atan2(y, x));

       // Parabolic altitude arc (peak at midpoints)
  //     const altitude = baseAltitude + Math.sin(Math.PI * f) * (maxAltitude - baseAltitude);
  //     coords.push(`${lon},${lat},${altitude.toFixed(2)}`);
  //   }

  //   return coords.join('\n');
  // }

}
